import { StandardObject } from "utils";
import {
  handleResp,
  getDefaultHeaders,
  extendFetchOptions,
  getURLWithQueryParams,
} from "./utils";
import type {
  CommonResponse,
  Sortable,
  WrappedCashifyPageableQuery,
  WrappedCashifyFunction,
} from "./utils";

export interface TenantedServiceHealthService {
  listTenantedServiceHealth: ReturnType<typeof listTenantedServiceHealth>;
  deleteTenantedServiceHealthRecord: ReturnType<
    typeof deleteTenantedServiceHealthRecord
  >;
  deregisterTenantedService: ReturnType<typeof deregisterTenantedService>;
  purgeOutdatedTenantedServiceHealthRecord: ReturnType<
    typeof purgeOutdatedTenantedServiceHealthRecord
  >;
}

type TenantedServiceHealthStatus = "UP" | "DOWN" | "UNKNOWN";
export interface TenantedServiceHealthSearchCriteria extends Sortable {
  instanceName?: string;
  instanceIp?: string;
  serviceName?: string;
  serviceVersion?: string;
  tenantedServiceName?: string;
  tenantedServiceConfigurationName?: string;
  tenantedServiceConfigurationRevision?: number;
  status?: TenantedServiceHealthStatus[];
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
}

export interface TenantedServiceHealthDTO {
  id: number;
  instanceName: string;
  instanceIp: string;
  serviceName: string;
  serviceVersion: string;
  tenantedServiceName: string;
  tenantedServiceConfigurationName: string;
  tenantedServiceConfigurationRevision: number;
  status: TenantedServiceHealthStatus;
  healthCheckResult: StandardObject;
  currentStatusSince: string;
  lastUpdateTime: string;
}

export const listTenantedServiceHealth: WrappedCashifyPageableQuery<
  TenantedServiceHealthSearchCriteria,
  TenantedServiceHealthDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/tenant/tenanted-service/health`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteTenantedServiceHealthRecord: WrappedCashifyFunction<
  { instanceName: string; tenantedServiceName: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ instanceName, tenantedServiceName }) => {
    let url = `${urlPrefix}/api/v1/tenant/tenanted-service/health/${instanceName}/${tenantedServiceName}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deregisterTenantedService: WrappedCashifyFunction<
  { instanceName: string; tenantedServiceName: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ instanceName, tenantedServiceName }) => {
    let url = `${urlPrefix}/api/v1/tenant/tenanted-service/instance/${instanceName}/${tenantedServiceName}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const purgeOutdatedTenantedServiceHealthRecord: WrappedCashifyFunction<
  { queryParams: TenantedServiceHealthSearchCriteria },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ queryParams }) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/tenant/tenanted-service/health/outdated`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
